import React, { useState, useEffect, useCallback } from 'react'
import Layout from '../components/Layout'
import { UserModal, PixLabelInviteModal } from '../components/modals'
import UserDataTable from '../components/UserDataTable'
import { userKanriService } from '../services/userKanriService'
import { useApiError } from '../../shared/hooks/useApiError'
import MessageDialog from '../../shared/ui/MessageDialog'
import Button from '../../shared/ui/Button'

const UserKanri = () => {
  const [users, setUsers] = useState([])
  const [userCount, setUserCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isCompanyInfoLoading, setIsCompanyInfoLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  
  // Error handling
  const { showErrorDialog, errorMessage, handleApiError, handleApiResponse, handleValidationErrors, closeErrorDialog } = useApiError()
  
  // Company info
  const [companyInfo, setCompanyInfo] = useState({
    riyoPlan: '',
    riyoPlanName: '',
    useStorage: '0.0GB',
    maxStorage: '',
    maxUser: '',
    companyName: '',
    pixClientUsage: 0,
    pixCameraUsage: 0,
    voiceRecognitionUsage: 0,
    ocrUsage: 0,
    thetaCameraUsage: 0
  })

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await userKanriService.getPixUserList()
      
      // Check for API response errors using useApiError
      if (handleApiResponse(response, 'ユーザー一覧の取得に失敗しました。')) {
        return // Error was handled, exit early
      }
      
      // Extract user list from response
      const userList = response?.pixUserList || []
      
      // Filter and process users like in the original jQuery code
      const filteredUsers = userList
        .filter(user => user && user.hihyojiFlg === 0)
        .map(user => ({
          ...user,
          // Add any additional processing needed
        }))
      
      setUsers(filteredUsers)
      
      // Count active users
      const activeCount = filteredUsers.filter(user => 
        user.hihyojiFlg === 0 && user.deleteFlg === '0'
      ).length
      setUserCount(activeCount)
      
    } catch (error) {
      console.error('Failed to load users:', error)
      
      // If it's a network error or API is not available, show a more specific message
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        handleApiError(error, 'APIサーバーに接続できません。ネットワーク接続を確認してください。')
      } else {
        handleApiError(error, 'ユーザー一覧の読み込みに失敗しました。')
      }
    } finally {
      setIsLoading(false)
    }
  }, [handleApiResponse, handleApiError])

  const loadCompanyInfo = useCallback(async () => {
    try {
      setIsCompanyInfoLoading(true)
      const response = await userKanriService.getCompanyInfo()
      
      // Check for API response errors using useApiError
      if (handleApiResponse(response, '企業情報の取得に失敗しました。')) {
        return // Error was handled, exit early
      }
      
      setCompanyInfo({
        riyoPlan: response.riyoPlan || '0',
        riyoPlanName: response.riyoPlanName || '',
        useStorage: response.useStorage || '0.0GB',
        maxStorage: response.maxStorage || '無制限',
        maxUser: response.maxUser || '無制限',
        companyName: response.companyName || '',
        pixClientUsage: response.pixClientUsage ?? 0,
        pixCameraUsage: response.pixCameraUsage ?? 0,
        voiceRecognitionUsage: response.voiceRecognitionUsage ?? 0,
        ocrUsage: response.ocrUsage ?? 0,
        thetaCameraUsage: response.thetaCameraUsage ?? 0
      })
    } catch (error) {
      console.error('Failed to load company info:', error)
      handleApiError(error, '企業情報の読み込みに失敗しました。')
    } finally {
      setIsCompanyInfoLoading(false)
    }
  }, [handleApiError, handleApiResponse])

  useEffect(() => {
    loadUsers()
    loadCompanyInfo()
  }, [loadUsers, loadCompanyInfo])

  const handleAddUser = useCallback(() => {
    // Check user limit based on plan
    const maxUsers = {
      '1': 1,
      '2': 5,
      '3': 30,
      '4': Infinity
    }
    
    if (userCount >= maxUsers[companyInfo.riyoPlan]) {
      handleApiError(new Error('User limit exceeded'), '追加出来るユーザーの数を超えています。')
      return
    }
    
    setEditingUser(null)
    setShowAddModal(true)
  }, [userCount, companyInfo.riyoPlan, handleApiError])

  const handleInvitePixLabelUser = useCallback(() => {
    // Check user limit based on plan
    const maxUsers = {
      '1': 1,
      '2': 5,
      '3': 30,
      '4': Infinity
    }
    
    if (userCount >= maxUsers[companyInfo.riyoPlan]) {
      handleApiError(new Error('User limit exceeded'), '追加出来るユーザーの数を超えています。')
      return
    }
    
    setShowInviteModal(true)
  }, [userCount, companyInfo.riyoPlan, handleApiError])

  const handleEditUser = useCallback((user) => {
    setEditingUser(user)
    setShowAddModal(true)
  }, [])

  const handleSaveUser = useCallback(async () => {
    loadUsers() // Reload the user list
  }, [loadUsers])

  const handleInviteSuccess = useCallback(() => {
    loadUsers() // Reload the user list
  }, [loadUsers])

  return (
    <Layout pageTitle="ユーザー管理">
      <div className="bg-white p-6 m-5">
        {/* Action Buttons and Company Info */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex space-x-4">
            <Button
              onClick={handleAddUser}
              variant="primary"
              size="md"
            >
              追加
            </Button>
            <Button
              onClick={handleInvitePixLabelUser}
              variant="primary"
              size="md"
            >
              すでにpix-labelをご利用中の方を招待する場合
            </Button>
          </div>
          
          <div className="text-sm text-gray-700 text-right self-end">
            {isCompanyInfoLoading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>企業情報読み込み中...</span>
              </div>
            ) : (
              <>
                ご利用プラン:{companyInfo.riyoPlanName} (使用容量:{companyInfo.useStorage}/{companyInfo.maxStorage} 利用人数:{userCount}人/{companyInfo.maxUser}) 企業名: {companyInfo.companyName}
              </>
            )}
          </div>
        </div>

        {/* User Data Table */}
        <div className="bg-gray-100 border border-gray-300 rounded">
          {isLoading || isCompanyInfoLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">読み込み中...</span>
            </div>
          ) : (
            <UserDataTable 
              users={users} 
              onEditUser={handleEditUser}
              companyInfo={companyInfo}
            />
          )}
        </div>

        {/* Add/Edit User Modal */}
        <UserModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false)
            setEditingUser(null) // Reset editing user when modal closes
          }}
          onSave={handleSaveUser}
          user={editingUser}
          isEdit={!!editingUser}
          userCount={userCount}
          riyoPlan={companyInfo.riyoPlan}
          companyInfo={companyInfo}
          onError={handleApiError}
          onValidationError={handleValidationErrors}
        />

        {/* Pix-label Invite Modal */}
        <PixLabelInviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
          userCount={userCount}
          riyoPlan={companyInfo.riyoPlan}
          onError={handleApiError}
          onValidationError={handleValidationErrors}
        />

        {/* Error Dialog */}
        <MessageDialog
          isOpen={showErrorDialog}
          title="エラー"
          message={errorMessage}
          type="error"
          onClose={closeErrorDialog}
        />
      </div>
    </Layout>
  )
}

export default UserKanri
